import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setCurrentFolder } from '@app/store/reducers/dropbox';
import * as dropboxService from '@app/services/dropbox';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faFolder, faFile, faTimes } from '@fortawesome/free-solid-svg-icons';

interface SearchResult {
  id: string;
  name: string;
  type: 'file' | 'folder';
  folder_id: string | null;
  path?: string;
  size_bytes?: number;
  mime_type?: string;
}

interface GlobalSearchProps {
  onFileSelect?: (file: any) => void;
  onFolderSelect?: (folderId: string) => void;
}

const GlobalSearch: React.FC<GlobalSearchProps> = ({ onFileSelect, onFolderSelect }) => {
  const dispatch = useDispatch();
  const folders = useSelector((state: any) => state.dropbox.folders);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Build folder path from folder hierarchy
  const buildFolderPath = (folderId: string | null): string => {
    if (!folderId) return '/';
    
    const path: string[] = [];
    let currentId = folderId;
    
    // Traverse up the folder tree
    while (currentId) {
      const folder = folders.find((f: any) => f.id === currentId);
      if (folder) {
        path.unshift(folder.name);
        currentId = folder.parent_id;
      } else {
        break;
      }
    }
    
    return path.length > 0 ? '/' + path.join('/') : '/';
  };

  // Debounced search
  useEffect(() => {
    if (query.length < 2) {
      setResults([]);
      setShowResults(false);
      return;
    }

    const timer = setTimeout(() => {
      performSearch(query);
    }, 300);

    return () => clearTimeout(timer);
  }, [query]);

  // Click outside to close
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setShowResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const performSearch = async (searchQuery: string) => {
    setLoading(true);
    try {
      const response = await dropboxService.searchFiles({
        query: searchQuery
      });

      if (response?.success) {
        // Combine files and folders into a single array with computed paths
        const combinedResults: SearchResult[] = [
          ...(response.data.folders || []).map((folder: any) => ({
            ...folder,
            type: 'folder' as const,
            path: buildFolderPath(folder.parent_id)
          })),
          ...(response.data.files || []).map((file: any) => ({
            ...file,
            type: 'file' as const,
            path: buildFolderPath(file.folder_id)
          }))
        ];
        setResults(combinedResults);
        setShowResults(true);
      }
    } catch (error) {
      // Search failed silently
    } finally {
      setLoading(false);
    }
  };

  const handleResultClick = (result: SearchResult) => {
    if (result.type === 'folder') {
      dispatch(setCurrentFolder(result.id));
      if (onFolderSelect) {
        onFolderSelect(result.id);
      }
    } else {
      if (onFileSelect) {
        onFileSelect(result);
      }
      // Navigate to the folder containing the file
      if (result.folder_id) {
        dispatch(setCurrentFolder(result.folder_id));
      }
    }
    setQuery('');
    setShowResults(false);
  };

  const handleClear = () => {
    setQuery('');
    setResults([]);
    setShowResults(false);
    inputRef.current?.focus();
  };

  const formatFileSize = (bytes?: number) => {
    if (!bytes) return '';
    const mb = bytes / 1024 / 1024;
    if (mb < 1) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${mb.toFixed(1)} MB`;
  };

  const getFileIcon = (result: SearchResult) => {
    if (result.type === 'folder') {
      return <FontAwesomeIcon icon={faFolder} className="text-warning" />;
    }
    return <FontAwesomeIcon icon={faFile} className="text-primary" />;
  };

  return (
    <div className="global-search" ref={searchRef}>
      <div className="search-input-wrapper">
        <FontAwesomeIcon icon={faSearch} className="search-icon" />
        <input
          ref={inputRef}
          type="text"
          className="form-control search-input"
          placeholder="Search files and folders..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => query.length >= 2 && setShowResults(true)}
        />
        {query && (
          <button className="clear-button" onClick={handleClear}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        )}
        {loading && (
          <div className="search-spinner">
            <div className="spinner-border spinner-border-sm" role="status">
              <span className="sr-only">Searching...</span>
            </div>
          </div>
        )}
      </div>

      {showResults && results.length > 0 && (
        <div className="search-results-dropdown">
          <div className="search-results-header">
            <i className="fas fa-search mr-2"></i>
            Found {results.length} result{results.length !== 1 ? 's' : ''}
          </div>
          <ul className="search-results-list">
            {results.map((result) => {
              const fullPath = result.path || '/';
              const displayPath = fullPath === '/' ? 'Root folder' : fullPath;
              
              return (
                <li
                  key={`${result.type}-${result.id}`}
                  className="search-result-item"
                  onClick={() => handleResultClick(result)}
                >
                  <div className="result-icon">{getFileIcon(result)}</div>
                  <div className="result-details">
                    <div className="result-name" title={result.name}>
                      {result.name}
                    </div>
                    {result.type === 'file' && result.size_bytes && (
                      <div className="result-size">
                        <i className="fas fa-file-alt mr-1"></i>
                        {formatFileSize(result.size_bytes)}
                      </div>
                    )}
                    <div className="result-path" title={displayPath}>
                      <i className="fas fa-folder mr-1"></i>
                      {displayPath}
                    </div>
                  </div>
                  <div className="result-type-badge">
                    {result.type === 'folder' ? 'Folder' : 'File'}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {showResults && query.length >= 2 && results.length === 0 && !loading && (
        <div className="search-results-dropdown">
          <div className="search-no-results">
            <p>No results found for "{query}"</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default GlobalSearch;
